import { tracking } from '@iwoca/frontend-tracking-library';
import { ContentSwitcher } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import { useGetSellerCustomers } from '../../../../api/lending/lapiHooks';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import {
  TApplicationStage,
  TCustomersSwitcherStatus,
  TSpendingLimitStatus,
} from '../../Customers.types';

const statuses: TCustomersSwitcherStatus[] = [
  {
    status: 'offer_approved',
    label: 'Ready to use',
    pipColour: 'bg-success-mid',
  },
  {
    status: 'last_approved_offer',
    label: 'Ready to refresh',
    pipColour: 'bg-warning',
  },
  {
    status: 'post_offer',
    label: 'Eligible',
    pipColour: 'bg-primary-40',
  },
];

const handleTracking = (status: TSpendingLimitStatus['value'] | 'ALL') => {
  switch (status) {
    case 'ALL':
      tracking.ampli.customersPageFilterButtonClickedAllLimits();
      break;
    case 'offer_approved':
      tracking.ampli.customersPageFilterButtonClickedOfferApproved();
      break;
    case 'last_approved_offer':
      tracking.ampli.customersPageFilterButtonClickedLastApprovedOffer();
      break;
    case 'post_offer':
      tracking.ampli.customersPageFilterButtonClickedPostOffer();
      break;
  }
};

export const CustomersContentSwitcher = ({
  updateQueryParams,
}: {
  updateQueryParams: (params: {
    status: TSpendingLimitStatus['value'] | null;
    stage: TApplicationStage['value'] | null;
  }) => void;
}) => {
  const methods = useFormContext();
  const [searchParams] = useSearchParams();
  const currentStatusFilter = searchParams.get('status') || 'ALL';
  const { isMobile } = useIsMobile({ maxWidth: 1024 });

  const currentStageFilter = searchParams.get('stage') || null;

  const { metadata } = useGetSellerCustomers({
    page: 1,
    ...(currentStageFilter && { application_stage: currentStageFilter }),
  });

  return (
    <ContentSwitcher
      value={currentStatusFilter}
      className="m:!h-max m:!w-full m:!justify-between"
    >
      <ContentSwitcher.Button
        id="ALL"
        className="flex cursor-pointer xs:!px-s m:!h-max m:flex-col m:items-center m:justify-center m:gap-s m:!text-xs"
        onClick={() => {
          methods.setValue('status', null);
          updateQueryParams({
            status: null,
            stage: methods.getValues('stage'),
          });
          handleTracking('ALL');
        }}
      >
        {isMobile && (
          <div
            className={`mr-s flex h-[20px] min-w-[33px] items-center justify-center rounded-s bg-secondary-80 px-xs text-s font-bold text-primary-40 m:m-0`}
          >
            {(metadata?.count || 0).toLocaleString('en-GB')}
          </div>
        )}
        {isMobile ? 'All' : 'All limits'}
      </ContentSwitcher.Button>
      {statuses.map((status) => (
        <ContentSwitcher.Button
          key={status.status}
          id={status.status}
          className="flex cursor-pointer xs:!px-s m:!h-max m:flex-col m:items-center m:justify-center m:gap-s m:!text-xs"
          onClick={() => {
            methods.setValue('status', status.status);
            updateQueryParams({
              status: status.status,
              stage: methods.getValues('stage'),
            });
            handleTracking(status.status);
          }}
        >
          <div
            className={clsx(
              `mr-s flex h-[20px] min-w-[33px] items-center justify-center rounded-s px-xs text-s font-bold text-structure-100 m:m-0`,
              status.pipColour,
            )}
          >
            {(
              metadata?.totals_by_spending_limit_status?.[status.status] || 0
            ).toLocaleString('en-GB')}
          </div>
          {status.label}
        </ContentSwitcher.Button>
      ))}
    </ContentSwitcher>
  );
};
